/**
 * Resilient list parsing.
 *
 * Every repository's `parse()` throws (Zod `.parse`) when a document doesn't
 * match its schema. Used directly in `snap.docs.map(parse)`, a SINGLE malformed
 * document makes the whole `.map` throw — which blanks the ENTIRE list (public
 * catalogue, admin tables, member space). This actually happened once: a stray
 * `null` in a non-nullable inscription field showed "Aucune inscription" while
 * Firestore held 155 docs (see HANDOFF §11).
 *
 * `parseDocs` isolates each document: a doc that fails to parse is skipped and
 * logged, the rest render normally. No behaviour change for valid data.
 */

type MinimalDoc = { id: string; data: () => unknown };

export function parseDocs<T>(
  docs: readonly MinimalDoc[],
  parseOne: (data: unknown, id: string) => T,
  collectionName: string,
): T[] {
  const out: T[] = [];
  for (const d of docs) {
    try {
      out.push(parseOne(d.data(), d.id));
    } catch (err) {
      // Non-fatal: skip the bad doc so one corrupt record can't blank the list.
      // eslint-disable-next-line no-console
      console.warn(`[${collectionName}] skipped malformed doc "${d.id}":`, err);
    }
  }
  return out;
}
